import { TNumStr } from "cakemail-ui-components-v2"
import { ChartDatasetProperties } from "chart.js"
import { TChartPoint, TRegressionResult } from "./types"

// Linear Regression Calculation
// Calculate the slope (m) and intercept (b) for the trend line
export function calculateTrendLine(data: TChartPoint[]): TRegressionResult {
    const n = data.length

    if (n === 0) {
        return { m: 0, b: 0 }
    }

    const sumX = data.reduce((sum, point) => sum + point.x, 0)
    const sumY = data.reduce((sum, point) => sum + point.y, 0)
    const sumXY = data.reduce((sum, point) => sum + point.x * point.y, 0)
    const sumX2 = data.reduce((sum, point) => sum + point.x * point.x, 0)

    const meanX = sumX / n
    const meanY = sumY / n

    const m = (sumXY - n * meanX * meanY) / (sumX2 - n * meanX * meanX)
    const b = meanY - m * meanX

    return { m, b }
}

// Generate Trend Line Points
export function generateTrendLine(
    data: TChartPoint[],
    regression: TRegressionResult
): TChartPoint[] {
    if (data.length === 0) {
        return []
    }

    const xValues = data.map((point) => point.x)
    const minX = Math.min(...xValues)
    const maxX = Math.max(...xValues)

    // Generate trend line points (at the start and end of the range)
    return [
        { x: minX, y: Math.max(0, regression.m * minX + regression.b) },
        { x: maxX, y: Math.max(0, regression.m * maxX + regression.b) },
    ]
}


export function calculateMovingAverage(data: TChartPoint[], windowSize: number): TChartPoint[] {
    if (data.length < windowSize) {
        throw new Error("Window size cannot be larger than the data length.")
    }

    const result: TChartPoint[] = []
    for (let i = 0; i < data.length; i++) {
        // Calculate the window range
        const start = Math.max(0, i - Math.floor(windowSize / 2))
        const end = Math.min(data.length, i + Math.ceil(windowSize / 2))

        // Extract only the Y values from the window
        const window = data.slice(start, end).map((point) => point.y)

        // Calculate the average Y for the window
        const avgY = window.reduce((sum, y) => sum + y, 0) / window.length

        // Keep the X value the same and use the averaged Y value
        result.push({ x: data[i].x, y: avgY })
    }

    return result
}